import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, Store, MapPin, Clock, Star, Zap, Download } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';


const NAV_ITEMS = [
  { label: 'Features', section: 'features', type: 'section' },
  { label: 'For Vendors', to: '/vendors', type: 'page', icon: Store },
  { label: 'Blog', to: '/blog', type: 'page' },
  { label: 'About', to: '/about', type: 'page' },
  { label: 'Contact', to: '/contact', type: 'page' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll listener for header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Section highlight on scroll (home page only)
  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }
    const sectionIds = ['features', 'about'];
    const handleScroll = () => {
      let found = '';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom > 120) {
            found = id;
            break;
          }
        }
      }
      setActiveSection(found);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const handleSectionClick = (section: string) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: section } });
    } else {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const isActive = (item: any) => {
    if (item.type === 'page') {
      return location.pathname.startsWith(item.to);
    }
    if (item.type === 'section' && location.pathname === '/') {
      return activeSection === item.section;
    }
    return false;
  };

  return (
    <motion.header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 w-full ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-lg' 
          : 'bg-white/80 backdrop-blur-md'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      {/* Top notification bar */}
      <motion.div 
        className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-center py-2 text-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-center justify-center gap-2">
          <Zap className="w-4 h-4" />
          <span className="font-medium">🚀 India's affordable quick commerce platform - Coming Soon!</span>
          <Star className="w-4 h-4 fill-current" />
        </div>
      </motion.div>

      <div className="container mx-auto px-4 overflow-hidden">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Enhanced Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <img 
                    src="/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png" 
                    alt="Dailzy" 
                    className="w-6 h-6"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-extrabold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent tracking-tight">
                  Dailzy
                </span>
                <span className="text-xs text-gray-500 -mt-1 hidden sm:block">Too busy? Do dailzy</span>
              </div>
            </Link>
          </motion.div>

          {/* Location & Delivery Info - Desktop */}
          <motion.div 
            className="hidden lg:flex items-center gap-4 text-sm"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors cursor-pointer">
              <MapPin className="w-4 h-4 text-red-500" />
              <span className="text-gray-700 font-medium">All India</span>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <Clock className="w-4 h-4" />
              <span className="font-medium">10-15 min delivery</span>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {NAV_ITEMS.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                {item.type === 'page' ? (
                  <Link
                    to={item.to}
                    className={`relative px-4 py-2 rounded-lg transition-all duration-300 font-medium flex items-center group ${
                      isActive(item) 
                        ? 'text-red-600 bg-red-50' 
                        : 'text-gray-700 hover:text-red-500 hover:bg-red-50'
                    }`}
                  >
                    {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                    {item.label}
                    {isActive(item) && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                        layoutId="activeTab"
                      />
                    )}
                  </Link>
                ) : (
                  <button
                    onClick={() => handleSectionClick(item.section)}
                    className={`relative px-4 py-2 rounded-lg transition-all duration-300 font-medium group ${
                      isActive(item) 
                        ? 'text-red-600 bg-red-50' 
                        : 'text-gray-700 hover:text-red-500 hover:bg-red-50'
                    }`}
                  >
                    {item.label}
                    {isActive(item) && (
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                        layoutId="activeTab"
                      />
                    )}
                  </button>
                )}
              </motion.div>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <motion.div
            className="hidden md:block"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.0 }}
          >
            <Button 
              onClick={() => {
                const event = new CustomEvent('openAppModal');
                window.dispatchEvent(event);
              }}
              className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <Download className="w-4 h-4 mr-2" />
              Get App
            </Button>
          </motion.div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden text-gray-600 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-50"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <AnimatePresence mode="wait">
              {isMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X size={28} />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu size={28} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Enhanced Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              className="md:hidden py-6 bg-white/95 backdrop-blur-xl rounded-b-2xl shadow-2xl mt-2 border border-gray-200"
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {/* Location Info - Mobile */}
              <div className="px-4 mb-4">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-500" />
                    <span className="text-gray-700 font-medium">All India</span>
                  </div>
                  <div className="flex items-center gap-2 text-green-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">10-15 min</span>
                  </div>
                </div>
              </div>

              {/* Navigation Items */}
              <div className="space-y-1 px-4">
                {NAV_ITEMS.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {item.type === 'page' ? (
                      <Link
                        to={item.to}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-300 font-medium ${
                          isActive(item) 
                            ? 'text-red-600 bg-red-50 border-l-4 border-red-500' 
                            : 'text-gray-700 hover:text-red-500 hover:bg-red-50'
                        }`}
                      >
                        {item.icon && <item.icon className="w-5 h-5 mr-3" />}
                        {item.label}
                      </Link>
                    ) : (
                      <button
                        onClick={() => handleSectionClick(item.section)}
                        className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-300 font-medium ${
                          isActive(item) 
                            ? 'text-red-600 bg-red-50 border-l-4 border-red-500' 
                            : 'text-gray-700 hover:text-red-500 hover:bg-red-50'
                        }`}
                      >
                        {item.label}
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* CTA Button - Mobile */}
              <div className="px-4 mt-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Button 
                    onClick={() => {
                      const event = new CustomEvent('openAppModal');
                      window.dispatchEvent(event);
                      setIsMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white px-6 py-4 rounded-xl font-semibold shadow-lg text-lg"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Get App
                  </Button>
                </motion.div>
              </div>

            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;